// ============================================
// FAQ.jsx - Professional AI-Powered Help Center
// Features: Perplexity-style search, Gemini AI chat, analytics, bookmarks
// ============================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Search, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, 
  Bookmark, BookmarkCheck, Copy, Share2, TrendingUp,
  MessageSquare, Sparkles, X, Send, Loader
} from 'lucide-react';

const FAQ = () => {
  // ===== CORE STATE =====
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [viewCounts, setViewCounts] = useState({});
  const [ratings, setRatings] = useState({});
  const [bookmarks, setBookmarks] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchHistory, setSearchHistory] = useState([]);
  
  // ===== AI CHAT STATE =====
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);

  // ===== FAQ DATA =====
  const faqs = useMemo(() => [
    {
      id: 1,
      category: 'Getting Started',
      question: 'How do I create an account?',
      answer: 'Click the "Sign Up" button in the top right corner. Fill in your email, create a password, and verify your email address. You can also sign up using Google or GitHub.',
      tags: ['account', 'signup', 'registration'],
      relatedIds: [2, 3]
    },
    {
      id: 2,
      category: 'Getting Started',
      question: 'What information do I need to get started?',
      answer: 'You need a valid email address, basic profile information, and for creators, links to your content channels. Investment information is optional initially.',
      tags: ['setup', 'requirements', 'onboarding'],
      relatedIds: [1, 4]
    },
    {
      id: 3,
      category: 'Getting Started',
      question: 'Is there a mobile app?',
      answer: 'Currently, we offer a mobile-responsive web app. Native iOS and Android apps are in development and will be available in Q2 2025.',
      tags: ['mobile', 'app', 'platform'],
      relatedIds: [1, 15]
    },
    {
      id: 4,
      category: 'Investments',
      question: 'What is the minimum investment amount?',
      answer: 'The minimum investment varies by project, typically ranging from $100 to $500. Each project listing shows the minimum investment required.',
      tags: ['investment', 'minimum', 'funding'],
      relatedIds: [5, 6, 7]
    },
    {
      id: 5,
      category: 'Investments',
      question: 'How do I invest in a project?',
      answer: 'Browse available projects, click "Invest Now", select your investment amount, review the terms, and complete payment. You\'ll receive confirmation and updates via email.',
      tags: ['investment', 'process', 'howto'],
      relatedIds: [4, 6, 8]
    },
    {
      id: 6,
      category: 'Investments',
      question: 'What payment methods are accepted?',
      answer: 'We accept credit/debit cards, bank transfers, PayPal, and cryptocurrency (BTC, ETH). All transactions are secure and encrypted.',
      tags: ['payment', 'methods', 'security'],
      relatedIds: [4, 5, 9]
    },
    {
      id: 7,
      category: 'Investments',
      question: 'Can I withdraw my investment?',
      answer: 'Investment terms vary by project. Some allow early withdrawal with penalties, while others have lock-in periods. Check each project\'s terms before investing.',
      tags: ['withdrawal', 'liquidity', 'terms'],
      relatedIds: [4, 5, 10]
    },
    {
      id: 8,
      category: 'Investments',
      question: 'How are returns calculated and distributed?',
      answer: 'Returns depend on project performance and are distributed monthly or quarterly. You can track your returns in the Portfolio dashboard. Payments are automated.',
      tags: ['returns', 'dividends', 'payout'],
      relatedIds: [5, 7, 11]
    },
    {
      id: 9,
      category: 'Security',
      question: 'Is my money safe?',
      answer: 'Yes. We use bank-level encryption, secure payment processors, and funds are held in segregated accounts. We\'re also insured and regulated.',
      tags: ['security', 'safety', 'protection'],
      relatedIds: [6, 12]
    },
    {
      id: 10,
      category: 'Security',
      question: 'How is my personal data protected?',
      answer: 'We comply with GDPR and use AES-256 encryption. Your data is never sold. Read our Privacy Policy for details.',
      tags: ['privacy', 'data', 'gdpr'],
      relatedIds: [9, 13]
    },
    {
      id: 11,
      category: 'Creators',
      question: 'How do I apply as a creator?',
      answer: 'Click "Apply as Creator", fill in your channel details, content niche, and revenue information. Our team reviews applications within 3-5 business days.',
      tags: ['creator', 'application', 'apply'],
      relatedIds: [12, 13]
    },
    {
      id: 12,
      category: 'Creators',
      question: 'What are the requirements for creators?',
      answer: 'Minimum 10K subscribers/followers, consistent content schedule, monetization enabled, and 6+ months of active content creation.',
      tags: ['creator', 'requirements', 'eligibility'],
      relatedIds: [11, 14]
    },
    {
      id: 13,
      category: 'Creators',
      question: 'How much equity do creators keep?',
      answer: 'Creators typically keep 60-80% equity depending on funding amount and terms. You maintain full creative control.',
      tags: ['creator', 'equity', 'ownership'],
      relatedIds: [11, 12]
    },
    {
      id: 14,
      category: 'Platform',
      question: 'What fees does the platform charge?',
      answer: 'Platform fee is 2-5% on successful investments. Creators pay 5-10% of revenue share. No hidden fees. All fees are disclosed upfront.',
      tags: ['fees', 'costs', 'pricing'],
      relatedIds: [4, 13]
    },
    {
      id: 15,
      category: 'Platform',
      question: 'How do I contact support?',
      answer: 'Use the chat widget (bottom right), email support@choicemcn.com, or submit a ticket. Average response time is under 2 hours.',
      tags: ['support', 'help', 'contact'],
      relatedIds: [3, 16]
    },
    {
      id: 16,
      category: 'Platform',
      question: 'Can I change my account settings?',
      answer: 'Yes, go to Settings > Profile to update your information, notification preferences, and security settings.',
      tags: ['settings', 'profile', 'account'],
      relatedIds: [2, 15]
    }
  ], []);

  // ===== LOAD PERSISTED DATA =====
  useEffect(() => {
    const savedViews = localStorage.getItem('faq_views');
    const savedRatings = localStorage.getItem('faq_ratings');
    const savedBookmarks = localStorage.getItem('faq_bookmarks');
    const savedHistory = localStorage.getItem('faq_search_history');
    
    if (savedViews) setViewCounts(JSON.parse(savedViews));
    if (savedRatings) setRatings(JSON.parse(savedRatings));
    if (savedBookmarks) setBookmarks(new Set(JSON.parse(savedBookmarks)));
    if (savedHistory) setSearchHistory(JSON.parse(savedHistory));
  }, []);

  // ===== SAVE TO LOCALSTORAGE =====
  useEffect(() => {
    localStorage.setItem('faq_views', JSON.stringify(viewCounts));
  }, [viewCounts]);

  useEffect(() => {
    localStorage.setItem('faq_ratings', JSON.stringify(ratings));
  }, [ratings]);

  useEffect(() => {
    localStorage.setItem('faq_bookmarks', JSON.stringify([...bookmarks]));
  }, [bookmarks]);

  useEffect(() => {
    localStorage.setItem('faq_search_history', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // ===== AI SEARCH LOGIC =====
  const calculateRelevanceScore = useCallback((faq, query) => {
    if (!query) return 0;
    
    const lowerQuery = query.toLowerCase();
    const lowerQuestion = faq.question.toLowerCase();
    const lowerAnswer = faq.answer.toLowerCase();
    const lowerTags = faq.tags.join(' ').toLowerCase();
    
    let score = 0;
    
    // Exact question match (highest priority)
    if (lowerQuestion === lowerQuery) score += 100;
    
    // Question contains query
    if (lowerQuestion.includes(lowerQuery)) score += 50;
    
    // Answer contains query
    if (lowerAnswer.includes(lowerQuery)) score += 30;
    
    // Tags match
    faq.tags.forEach(tag => {
      if (lowerQuery.includes(tag) || tag.includes(lowerQuery)) score += 20;
    });
    
    // Word-by-word matching
    const queryWords = lowerQuery.split(' ');
    queryWords.forEach(word => {
      if (word.length > 2) {
        if (lowerQuestion.includes(word)) score += 10;
        if (lowerAnswer.includes(word)) score += 5;
        if (lowerTags.includes(word)) score += 5;
      }
    });
    
    return score;
  }, []);

  const aiSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const results = faqs.map(faq => ({
      faq,
      score: calculateRelevanceScore(faq, searchQuery),
      confidence: Math.min(calculateRelevanceScore(faq, searchQuery) / 100 * 100, 95)
    }))
    .filter(result => result.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);
    
    return results;
  }, [searchQuery, faqs, calculateRelevanceScore]);

  // ===== FILTERED FAQS =====
  const filteredFAQs = useMemo(() => {
    let filtered = faqs;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const results = aiSearchResults.map(r => r.faq);
      filtered = results.length > 0 ? results : filtered;
    }
    
    return filtered;
  }, [faqs, selectedCategory, searchQuery, aiSearchResults]);

  // ===== TRACK VIEW =====
  const trackView = useCallback((id) => {
    setViewCounts(prev => ({
      ...prev,
      [id]: (prev[id] || 0) + 1
    }));
  }, []);

  // ===== TOGGLE FAQ =====
  const toggleFAQ = useCallback((id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        trackView(id);
      }
      return newSet;
    });
  }, [trackView]);

  // ===== RATING =====
  const handleRating = useCallback((id, isPositive) => {
    setRatings(prev => ({
      ...prev,
      [id]: { positive: isPositive, timestamp: Date.now() }
    }));
  }, []);

  // ===== BOOKMARK =====
  const toggleBookmark = useCallback((id) => {
    setBookmarks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // ===== SEARCH HANDLER =====
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (query.trim() && !searchHistory.includes(query.trim())) {
      const newHistory = [query.trim(), ...searchHistory].slice(0, 10);
      setSearchHistory(newHistory);
    }
  }, [searchHistory]);

  // ===== AI CHAT WITH GEMINI =====
  const sendAIMessage = useCallback(async (message) => {
    if (!message.trim()) return;
    
    // Add user message
    const userMessage = { role: 'user', content: message, timestamp: Date.now() };
    setChatHistory(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsAIThinking(true);

    try {
      // Search FAQs for relevant context
      const relevantFAQs = faqs
        .map(faq => ({
          faq,
          score: calculateRelevanceScore(faq, message)
        }))
        .filter(r => r.score > 10)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);

      // Build context from FAQs
      const context = relevantFAQs.length > 0
        ? `Based on our FAQ database:\n\n${relevantFAQs.map(r => 
            `Q: ${r.faq.question}\nA: ${r.faq.answer}`
          ).join('\n\n')}`
        : 'I don\'t have specific FAQ information about that topic.';

      // Simulate AI response (replace with real Gemini API call)
      const aiResponse = await simulateGeminiResponse(message, context);
      
      const assistantMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: Date.now(),
        sources: relevantFAQs.map(r => r.faq.id)
      };
      
      setChatHistory(prev => [...prev, assistantMessage]);
      
      // Generate suggestions
      const suggestions = generateSuggestions(message);
      setAiSuggestions(suggestions);
      
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again or contact support.',
        timestamp: Date.now(),
        isError: true
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsAIThinking(false);
    }
  }, [faqs, calculateRelevanceScore]);

  // ===== SIMULATE GEMINI (Replace with real API) =====
  const simulateGeminiResponse = async (query, context) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Simple response logic (replace with real Gemini API call)
    if (context.includes('I don\'t have specific FAQ')) {
      return `I understand you're asking about "${query}". While I don't have a specific FAQ answer for this, I can help guide you. You might want to:\n\n1. Check our documentation at docs.choicemcn.com\n2. Contact our support team via the chat widget\n3. Browse related FAQs in the help center\n\nIs there anything else I can help you with?`;
    }
    
    return `${context}\n\nBased on this information, I hope I've answered your question about "${query}". Would you like to know more about any specific aspect?`;
  };

  const generateSuggestions = (query) => {
    const suggestions = [
      'Tell me more about fees',
      'How do returns work?',
      'What are the requirements?',
      'Is my data secure?'
    ];
    return suggestions.slice(0, 3);
  };

  // ===== CATEGORIES =====
  const categories = useMemo(() => {
    const cats = ['all', ...new Set(faqs.map(f => f.category))];
    return cats;
  }, [faqs]);

  // ===== POPULAR FAQS =====
  const popularFAQs = useMemo(() => {
    return [...faqs]
      .sort((a, b) => (viewCounts[b.id] || 0) - (viewCounts[a.id] || 0))
      .slice(0, 3);
  }, [faqs, viewCounts]);

  // ===== STATS =====
  const stats = useMemo(() => ({
    total: faqs.length,
    viewed: Object.keys(viewCounts).length,
    bookmarked: bookmarks.size,
    helpful: Object.values(ratings).filter(r => r.positive).length
  }), [faqs, viewCounts, bookmarks, ratings]);

  // ===== EXPAND/COLLAPSE ALL =====
  const expandAll = () => setExpandedIds(new Set(faqs.map(f => f.id)));
  const collapseAll = () => setExpandedIds(new Set());

  // ===== COPY FAQ =====
  const copyFAQ = (faq) => {
    const text = `Q: ${faq.question}\n\nA: ${faq.answer}`;
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  // ===== SHARE FAQ =====
  const shareFAQ = (faq) => {
    const url = `${window.location.origin}/faq#${faq.id}`;
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  return (
    <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Help Center
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Find answers instantly with AI-powered search
          </p>

          {/* Stats Dashboard */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-sm text-slate-600">Total FAQs</div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <div className="text-2xl font-bold text-green-600">{stats.viewed}</div>
              <div className="text-sm text-slate-600">Viewed</div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <div className="text-2xl font-bold text-purple-600">{stats.bookmarked}</div>
              <div className="text-sm text-slate-600">Bookmarked</div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <div className="text-2xl font-bold text-amber-600">{stats.helpful}</div>
              <div className="text-sm text-slate-600">Helpful</div>
            </div>
          </div>

          {/* AI Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Ask anything... (AI-powered)"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-24 py-4 text-lg border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none shadow-lg"
              />
              <button
                onClick={() => setShowAIChat(true)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:shadow-lg transition-all"
              >
                <Sparkles size={16} />
                AI Chat
              </button>
            </div>

            {/* AI Search Results */}
            {searchQuery && aiSearchResults.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-2xl border-2 border-blue-200 z-10 max-h-96 overflow-y-auto">
                <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                    <Sparkles size={16} />
                    AI-Powered Results ({aiSearchResults.length})
                  </div>
                </div>
                {aiSearchResults.map(({ faq, confidence }) => (
                  <div
                    key={faq.id}
                    onClick={() => {
                      toggleFAQ(faq.id);
                      setSearchQuery('');
                    }}
                    className="p-4 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-slate-900">{faq.question}</div>
                      <div className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                        {Math.round(confidence)}% match
                      </div>
                    </div>
                    <div className="text-sm text-slate-600 line-clamp-2">{faq.answer}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Search History */}
            {searchQuery === '' && searchHistory.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-lg border z-10">
                <div className="p-3 border-b bg-slate-50 text-xs font-semibold text-slate-600">
                  Recent Searches
                </div>
                {searchHistory.slice(0, 5).map((query, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSearch(query)}
                    className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 text-sm text-slate-700"
                  >
                    {query}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={expandAll}
            className="px-4 py-2 bg-white border rounded-lg hover:bg-slate-50 transition-colors text-sm"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-4 py-2 bg-white border rounded-lg hover:bg-slate-50 transition-colors text-sm"
          >
            Collapse All
          </button>
          {bookmarks.size > 0 && (
            <button
              onClick={() => {
                const bookmarked = faqs.filter(f => bookmarks.has(f.id));
                setExpandedIds(new Set(bookmarked.map(f => f.id)));
              }}
              className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
            >
              View Bookmarks ({bookmarks.size})
            </button>
          )}
        </div>

        {/* Popular FAQs */}
        {searchQuery === '' && (
          <div className="mb-8 bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-amber-600" size={20} />
              Popular Questions
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {popularFAQs.map(faq => (
                <div
                  key={faq.id}
                  onClick={() => toggleFAQ(faq.id)}
                  className="p-4 border-2 border-amber-200 rounded-lg cursor-pointer hover:border-amber-400 transition-colors bg-gradient-to-br from-amber-50 to-orange-50"
                >
                  <div className="font-semibold text-slate-900 mb-2">{faq.question}</div>
                  <div className="text-xs text-slate-600">{viewCounts[faq.id] || 0} views</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFAQs.map((faq) => {
            const isExpanded = expandedIds.has(faq.id);
            const rating = ratings[faq.id];
            const isBookmarked = bookmarks.has(faq.id);

            return (
              <div
                key={faq.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Question Header */}
                <div
                  onClick={() => toggleFAQ(faq.id)}
                  className="p-6 cursor-pointer flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-semibold px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {faq.category}
                      </span>
                      {viewCounts[faq.id] > 10 && (
                        <span className="text-xs font-semibold px-2 py-1 bg-amber-100 text-amber-700 rounded flex items-center gap-1">
                          <TrendingUp size={12} />
                          Popular
                        </span>
                      )}
                      {isBookmarked && (
                        <BookmarkCheck className="text-purple-600" size={16} />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {faq.question}
                    </h3>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="text-slate-400 flex-shrink-0" size={24} />
                  ) : (
                    <ChevronDown className="text-slate-400 flex-shrink-0" size={24} />
                  )}
                </div>

                {/* Answer Section */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t">
                    <div className="pt-4 text-slate-700 leading-relaxed mb-4">
                      {faq.answer}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {faq.tags.map(tag => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-4 pt-4 border-t">
                      <button
                        onClick={() => toggleBookmark(faq.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                          isBookmarked
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {isBookmarked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                        {isBookmarked ? 'Saved' : 'Save'}
                      </button>

                      <button
                        onClick={() => copyFAQ(faq)}
                        className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        <Copy size={16} />
                        Copy
                      </button>

                      <button
                        onClick={() => shareFAQ(faq)}
                        className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        <Share2 size={16} />
                        Share
                      </button>

                      <div className="ml-auto flex items-center gap-2">
                        <span className="text-sm text-slate-600">Was this helpful?</span>
                        <button
                          onClick={() => handleRating(faq.id, true)}
                          className={`p-2 rounded-lg transition-colors ${
                            rating?.positive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-100 text-slate-600 hover:bg-green-50'
                          }`}
                        >
                          <ThumbsUp size={16} />
                        </button>
                        <button
                          onClick={() => handleRating(faq.id, false)}
                          className={`p-2 rounded-lg transition-colors ${
                            rating?.positive === false
                              ? 'bg-red-100 text-red-700'
                              : 'bg-slate-100 text-slate-600 hover:bg-red-50'
                          }`}
                        >
                          <ThumbsDown size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Related Questions */}
                    {faq.relatedIds && faq.relatedIds.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="text-sm font-semibold text-slate-700 mb-2">
                          Related Questions:
                        </div>
                        <div className="space-y-2">
                          {faq.relatedIds.map(relId => {
                            const relFAQ = faqs.find(f => f.id === relId);
                            return relFAQ ? (
                              <button
                                key={relId}
                                onClick={() => toggleFAQ(relId)}
                                className="block text-sm text-blue-600 hover:text-blue-700 hover:underline text-left"
                              >
                                → {relFAQ.question}
                              </button>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {filteredFAQs.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🤔</div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No FAQs found
            </h3>
            <p className="text-slate-600 mb-4">
              Try different keywords or ask our AI assistant
            </p>
            <button
              onClick={() => setShowAIChat(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto hover:shadow-lg transition-all"
            >
              <Sparkles size={20} />
              Chat with AI Assistant
            </button>
          </div>
        )}

        {/* External Resources */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <a
            href="/docs"
            className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl hover:shadow-md transition-all"
          >
            <MessageSquare className="w-8 h-8 text-blue-600 mb-3" />
            <div className="font-semibold text-slate-900 mb-1">Documentation</div>
            <div className="text-sm text-slate-600">Comprehensive guides & tutorials</div>
          </a>
          <a
            href="/community"
            className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl hover:shadow-md transition-all"
          >
            <TrendingUp className="w-8 h-8 text-purple-600 mb-3" />
            <div className="font-semibold text-slate-900 mb-1">Community Forum</div>
            <div className="text-sm text-slate-600">Connect with other users</div>
          </a>
          <a
            href="/status"
            className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl hover:shadow-md transition-all"
          >
            <TrendingUp className="w-8 h-8 text-amber-600 mb-3" />
            <div className="font-semibold text-slate-900 mb-1">Status Page</div>
            <div className="text-sm text-slate-600">System status & updates</div>
          </a>
        </div>
      </div>

      {/* AI Chat Modal */}
      {showAIChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Sparkles size={24} />
                <h3 className="text-xl font-semibold">AI Assistant (Powered by Gemini)</h3>
              </div>
              <button
                onClick={() => setShowAIChat(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Chat History */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">
                    Hi! I'm your AI Assistant
                  </h4>
                  <p className="text-slate-600 mb-6">
                    Ask me anything about the platform, investments, or creators
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {[
                      'How do I invest?',
                      'What are the fees?',
                      'Is my money safe?',
                      'Creator requirements?'
                    ].map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => {
                          setCurrentMessage(suggestion);
                          sendAIMessage(suggestion);
                        }}
                        className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {chatHistory.map((message, idx) => (
                    <div
                      key={idx}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-4 rounded-2xl ${
                          message.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : message.isError
                            ? 'bg-red-50 text-red-900'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        <div className="whitespace-pre-wrap">{message.content}</div>
                        {message.sources && message.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-300 text-xs opacity-75">
                            Sources: {message.sources.length} related FAQs
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isAIThinking && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 p-4 rounded-2xl flex items-center gap-2">
                        <Loader className="animate-spin" size={16} />
                        <span className="text-slate-600">Thinking...</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Suggestions */}
            {aiSuggestions.length > 0 && (
              <div className="px-4 py-2 border-t bg-slate-50">
                <div className="text-xs text-slate-600 mb-2">Suggested follow-ups:</div>
                <div className="flex flex-wrap gap-2">
                  {aiSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentMessage(suggestion);
                        sendAIMessage(suggestion);
                      }}
                      className="px-3 py-1 bg-white text-slate-700 rounded-lg hover:bg-slate-100 transition-colors text-sm border"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendAIMessage(currentMessage)}
                  placeholder="Ask anything..."
                  disabled={isAIThinking}
                  className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none disabled:bg-slate-100"
                />
                <button
                  onClick={() => sendAIMessage(currentMessage)}
                  disabled={!currentMessage.trim() || isAIThinking}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send size={20} />
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FAQ;